import { FaBriefcase, FaCalendarAlt, FaCheckCircle, FaExternalLinkAlt } from 'react-icons/fa'
import { motion } from 'framer-motion'
import { experienceData } from '../../data'
import { FadeIn } from '../animations/ScrollAnimations'
import FloatingParticles from '../animations/FloatingParticles'

export default function Experience(): JSX.Element {
  return (
    <div className="section bg-portfolio-black relative overflow-hidden">
      {/* Floating Particles Background */}
      <FloatingParticles />
      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-portfolio-green/20 to-transparent"></div>
        <div className="absolute top-20 left-20 w-72 h-72 bg-portfolio-green/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-portfolio-green/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="text-center mb-16">
          <FadeIn direction="up" delay={0.2}>
            <motion.h2 
              className="heading-2"
              whileHover={{ scale: 1.02 }}
            >
              {experienceData.title} <span className="gradient-text">{experienceData.subtitle}</span>
            </motion.h2>
            <motion.p 
              className="text-large max-w-3xl mx-auto"
              whileHover={{ scale: 1.01 }}
            >
              {experienceData.description}
            </motion.p>
          </FadeIn>
        </div>

        <div className="space-y-12">
          {experienceData.experiences.map((experience, index) => (
            <motion.div 
              key={index} 
              className="card cursor-pointer"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ 
                scale: 1.02, 
                y: -5,
                boxShadow: "0 20px 40px rgba(149, 255, 49, 0.15)"
              }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Role Info */}
                <div className="space-y-4">
                  <motion.div 
                    className="flex items-center gap-3"
                    whileHover={{ x: 10 }}
                  >
                    <motion.div 
                      className="w-12 h-12 bg-green-400/20 rounded-lg flex items-center justify-center"
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <FaBriefcase className="text-green-400 text-xl" />
                    </motion.div>
                    <div>
                      <motion.h3 
                        className="text-xl font-semibold text-white"
                        whileHover={{ color: "#95FF31" }}
                      >
                        {experience.title}
                      </motion.h3>
                      <div className="flex items-center gap-2">
                        <motion.p 
                          className="text-green-400 font-medium"
                          whileHover={{ scale: 1.05 }}
                        >
                          {experience.company}
                        </motion.p>
                        {experience.website && (
                          <motion.a
                            href={experience.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-400 hover:text-green-300 transition-colors"
                            whileHover={{ scale: 1.2, rotate: 15 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <FaExternalLinkAlt className="text-sm" />
                          </motion.a>
                        )}
                      </div>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="flex items-center gap-3 text-gray-400"
                    whileHover={{ x: 5 }}
                  >
                    <FaCalendarAlt className="text-sm" />
                    <span className="text-sm">{experience.period}</span>
                  </motion.div>

                  <motion.p 
                    className="text-gray-300 text-sm leading-relaxed"
                    whileHover={{ x: 5 }}
                  >
                    {experience.description}
                  </motion.p>
                </div>

                {/* Right Column - Technologies & Achievements */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Technologies */}
                  <div>
                    <motion.h4 
                      className="text-white font-semibold mb-3"
                      whileHover={{ scale: 1.02 }}
                    >
                      Technologies Used
                    </motion.h4>
                    <div className="flex flex-wrap gap-2">
                      {experience.technologies.map((tech, techIndex) => (
                        <motion.span
                          key={techIndex}
                          className="px-3 py-1 bg-gray-800 text-gray-300 rounded-full text-sm border border-gray-700 cursor-pointer"
                          whileHover={{ 
                            scale: 1.1, 
                            backgroundColor: "#1a1a1a",
                            borderColor: "#95FF31",
                            color: "#95FF31"
                          }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {tech}
                        </motion.span>
                      ))}
                    </div>
                  </div>

                  {/* Key Achievements */}
                  <div>
                    <motion.h4 
                      className="text-white font-semibold mb-3"
                      whileHover={{ scale: 1.02 }}
                    >
                      Key Achievements
                    </motion.h4>
                    <div className="space-y-2">
                      {experience.achievements.map((achievement, achievementIndex) => (
                        <motion.div 
                          key={achievementIndex} 
                          className="flex items-start gap-3"
                          whileHover={{ x: 10 }}
                        >
                          <motion.div
                            whileHover={{ rotate: 360, scale: 1.2 }}
                            transition={{ duration: 0.6 }}
                          >
                            <FaCheckCircle className="text-green-400 text-sm mt-1 flex-shrink-0" />
                          </motion.div>
                          <motion.p 
                            className="text-gray-300 text-sm leading-relaxed"
                            whileHover={{ color: "#e5e7eb" }}
                          >
                            {achievement}
                          </motion.p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <motion.p 
            className="text-large mb-6"
            whileHover={{ scale: 1.02 }}
          >
            Ready to bring this expertise to your next project?
          </motion.p>
          <motion.a 
            href="#contact" 
            className="btn-primary"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            Let's Work Together
          </motion.a>
        </motion.div>
      </div>
    </div>
  )
} 